import React, { useState, useEffect } from "react";

/* ============================================================================
   MobileShell — the mobile design as real React, inline-styled.

   Same approach that finally worked for desktop: this IS the rendering code,
   not a description of it. Inline styles cannot lose a specificity fight
   against the app's existing CSS.

   Presentation only. No state that matters, no contract calls — everything
   arrives as props. Drop it in and wire your existing handlers.

   Breakpoint: render this instead of the desktop tree below 768px.
   ========================================================================== */

const M = {
  serif: "'Instrument Serif', Georgia, serif",
  mono: "'JetBrains Mono', ui-monospace, Menlo, monospace",
  sans: "'Space Grotesk', -apple-system, sans-serif",
  ink: "#16181A",
  inkMid: "#4A4A46",
  inkSoft: "#8E8E88",
  inkFaint: "#A5A5A0",
  ghost: "#C8C8C2",
  rule: "#E6E6E1",
  ruleSoft: "#F2F2EE",
  ruleInner: "#F5F5F1",
  accent: "#56AEF0",
  accentInk: "#08222F",
  link: "#3E8FCC",
  danger: "#C4462E",
  ok: "#3E8C68",
};

/* iOS safe areas. env() falls back to 0 on Android and desktop. */
const SAFE_TOP = "env(safe-area-inset-top, 0px)";
const SAFE_BOTTOM = "env(safe-area-inset-bottom, 0px)";

export const MobileBase = () => (
  <style>{`
    @keyframes m-spin { to { transform: rotate(360deg); } }
    @keyframes m-sheet { from { transform: translateY(100%); } to { transform: none; } }
    @keyframes m-fade { from { opacity: 0; } to { opacity: 1; } }
    .m-row:active { background: #FAFAF8 !important; }
    .m-cta:active { transform: scale(0.985); }
    .m-app *:focus-visible { outline: 2px solid ${M.ink}; outline-offset: 2px; }
    .m-amount-input::placeholder { color: ${M.ghost}; opacity: 1; }
    /* The global "input { font-size: 16px !important }" iOS anti-zoom rule
       (see note below) also inflates the recipient placeholder to 16px. Shrink
       just the placeholder so "Enter receiver address" reads smaller, while the
       input itself stays 16px so focusing it still doesn't trigger iOS zoom. */
    .m-recipient-input::placeholder { font-size: 12.5px !important; }
    /* App.css has a global "input, select, textarea { font-size: 16px
       !important }" below 768px (iOS auto-zoom prevention on focus) that
       otherwise stomps the inline 74px below — a class selector beats a
       type selector at the same !important tier regardless of source
       order, so this wins without fighting App.css directly. Comfortably
       over 16px too, so the anti-zoom behavior that rule exists for still
       holds. */
    .m-amount-input { font-size: 74px !important; }
  `}</style>
);

/* ------------------------------------------------------------------ header */

function Header({ connected, address, onWallet, showBack, onBack, onHome }) {
  return (
    <div
      style={{
        flex: "none",
        height: 52,
        padding: "0 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid #F0F0EC`,
      }}
    >
      {showBack ? (
        // Stands in for the logo while the keyboard is up and the bottom
        // nav is hidden (see isEditing) — otherwise there's no way
        // back to the other tabs except finishing or clearing the amount.
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 36,
            height: 36,
            marginLeft: -8,
            border: 0,
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke={M.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
      ) : (
        <img
          src="/stabletrustpaylogo.png"
          alt="Stabletrust Pay"
          onClick={onHome}
          title="Home"
          style={{ height: 18, width: "auto", display: "block", cursor: onHome ? "pointer" : "default" }}
        />
      )}
      <button
        type="button"
        onClick={onWallet}
        style={
          connected
            ? {
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 14px",
                border: `1px solid ${M.rule}`,
                borderRadius: 999,
                background: "transparent",
                fontFamily: M.mono,
                fontSize: 10.5,
                color: M.ink,
                cursor: "pointer",
              }
            : {
                padding: "10px 18px",
                border: 0,
                borderRadius: 999,
                background: M.ink,
                color: "#FFFFFF",
                fontFamily: M.sans,
                fontSize: 13.5,
                fontWeight: 500,
                cursor: "pointer",
              }
        }
      >
        <span>{connected ? address : "Connect"}</span>
        {connected && (
          <span
            style={{
              width: 0,
              height: 0,
              borderLeft: "4px solid transparent",
              borderRight: "4px solid transparent",
              borderTop: "4.5px solid #9A9A94",
            }}
          />
        )}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------- bottom nav */

const TABS = [
  ["transfer", "Transfer"],
  ["deposit", "Deposit"],
  ["withdraw", "Withdraw"],
  ["history", "History"],
];

// Thin-line glyphs standing in for the plain ↑ ↓ ↧ ≡ characters, which
// render inconsistently across platforms and read as an afterthought next
// to everything else here being drawn, not typed. Same stroke weight/caps
// throughout so the set reads as one family.
function NavIcon({ id, color }) {
  const common = {
    width: 19,
    height: 19,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (id) {
    case "transfer":
      // Outbound send: a line to the corner with an arrowhead, not a full
      // arrow shaft — lighter-weight than the deposit/withdraw glyphs since
      // this one has to read at a glance as "to someone", not "in/out".
      return (
        <svg {...common}>
          <path d="M7 17L17 7" />
          <path d="M9 7h8v8" />
        </svg>
      );
    case "deposit":
      // Arrow settling down onto a baseline.
      return (
        <svg {...common}>
          <path d="M12 4v10" />
          <path d="M8 10l4 4 4-4" />
          <path d="M5 19h14" />
        </svg>
      );
    case "withdraw":
      // Exact vertical mirror of deposit's glyph, not a different metaphor
      // — the pair should read as opposites at a glance.
      return (
        <svg {...common}>
          <path d="M12 20V10" />
          <path d="M8 14l4-4 4 4" />
          <path d="M5 5h14" />
        </svg>
      );
    case "history":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v4l3 2" />
        </svg>
      );
    default:
      return null;
  }
}

function BottomNav({ activeTab, onTab }) {
  return (
    <div
      style={{
        flex: "none",
        borderTop: `1px solid #ECECE7`,
        background: "#FFFFFF",
        padding: `0 4px ${SAFE_BOTTOM}`,
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
      }}
    >
      {TABS.map(([id, label]) => {
        const on = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onTab(id)}
            style={{
              position: "relative",
              minHeight: 56,
              padding: "12px 0 10px",
              border: 0,
              background: "transparent",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 5,
              cursor: "pointer",
            }}
          >
            {on && (
              <span
                style={{
                  position: "absolute",
                  top: -1,
                  left: 14,
                  right: 14,
                  height: 1.5,
                  background: M.ink,
                }}
              />
            )}
            <span style={{ display: "flex", lineHeight: 0 }}>
              <NavIcon id={id} color={on ? M.ink : "#B0B0AA"} />
            </span>
            <span
              style={{
                fontFamily: M.mono,
                fontSize: 8.5,
                letterSpacing: "0.14em",
                textTransform: "none",
                color: on ? M.ink : M.inkFaint,
              }}
            >
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------- CTA button */

function Cta({ label, enabled, onClick }) {
  return (
    <button
      type="button"
      className="m-cta"
      onClick={onClick}
      disabled={!enabled}
      style={{
        width: "100%",
        minHeight: 58,
        border: 0,
        borderRadius: 999,
        background: enabled ? M.accent : "#F1F1ED",
        color: enabled ? M.accentInk : "#AFAFA9",
        fontFamily: M.sans,
        fontSize: 16,
        fontWeight: 600,
        cursor: enabled ? "pointer" : "default",
        transition: "transform .08s",
      }}
    >
      {label}
    </button>
  );
}

/* ------------------------------------------------------------ bottom sheet */

export function Sheet({ title, actionLabel, onAction, children, maxHeight = "84%" }) {
  return (
    <>
      <div
        onClick={onAction}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 40,
          background: "rgba(22,24,26,0.34)",
          animation: "m-fade .16s ease both",
        }}
      />
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 41,
          background: "#FFFFFF",
          borderTop: `1px solid ${M.rule}`,
          maxHeight,
          display: "flex",
          flexDirection: "column",
          animation: "m-sheet .22s cubic-bezier(.2,.8,.2,1) both",
        }}
      >
        <div style={{ flex: "none", padding: "10px 0 0", display: "flex", justifyContent: "center" }}>
          <div style={{ width: 44, height: 4, background: "#DEDED8" }} />
        </div>
        <div
          style={{
            flex: "none",
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid #F0F0EC`,
          }}
        >
          <div style={{ fontFamily: M.serif, fontSize: 25 }}>{title}</div>
          <button
            type="button"
            onClick={onAction}
            style={{
              border: 0,
              background: "transparent",
              fontFamily: M.mono,
              fontSize: 10,
              letterSpacing: "0.16em",
              textTransform: "none",
              color: M.inkSoft,
              cursor: "pointer",
              minHeight: 44,
              padding: "0 4px",
            }}
          >
            {actionLabel}
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingBottom: `calc(20px + ${SAFE_BOTTOM})` }}>
          {children}
        </div>
      </div>
    </>
  );
}

/* One row inside a sheet — 64px minimum, thumb-sized. */
export function SheetRow({ iconUrl, round, name, sub, subMuted, selected, disabled, onClick }) {
  return (
    <button
      type="button"
      className={disabled ? "" : "m-row"}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        width: "100%",
        minHeight: 64,
        padding: "15px 20px",
        border: 0,
        borderBottom: `1px solid ${M.ruleInner}`,
        background: "transparent",
        display: "flex",
        alignItems: "center",
        gap: 14,
        textAlign: "left",
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          flex: "none",
          borderRadius: round ? "50%" : 7,
          background: iconUrl ? `url(${iconUrl}) center/contain no-repeat` : M.ruleSoft,
        }}
      />
      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={{ fontFamily: M.sans, fontSize: 16, fontWeight: 500 }}>{name}</span>
        <span
          style={{
            fontFamily: M.mono,
            fontSize: 9.5,
            letterSpacing: "0.14em",
            textTransform: "none",
            color: subMuted ? "#B0B0AA" : "#9A9A94",
          }}
        >
          {sub}
        </span>
      </span>
      {selected && (
        <span
          style={{
            width: 16,
            height: 9,
            flex: "none",
            marginTop: -4,
            borderLeft: `1.8px solid ${M.ink}`,
            borderBottom: `1.8px solid ${M.ink}`,
            transform: "rotate(-45deg)",
          }}
        />
      )}
    </button>
  );
}

/* ------------------------------------------------------- confirm sheet */

export function ConfirmSheet({ amount, token, rows, onConfirm, onCancel }) {
  return (
    <>
      <div
        onClick={onCancel}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 40,
          background: "rgba(22,24,26,0.34)",
          animation: "m-fade .16s ease both",
        }}
      />
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 41,
          background: "#FFFFFF",
          borderTop: `1px solid ${M.rule}`,
          animation: "m-sheet .22s cubic-bezier(.2,.8,.2,1) both",
        }}
      >
        <div style={{ padding: "10px 0 0", display: "flex", justifyContent: "center" }}>
          <div style={{ width: 44, height: 4, background: "#DEDED8" }} />
        </div>
        <div style={{ padding: "18px 20px 6px", display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <div style={{ fontFamily: M.serif, fontSize: 27 }}>Confirm transfer</div>
          <button
            type="button"
            onClick={onCancel}
            style={{
              border: 0,
              background: "transparent",
              fontFamily: M.mono,
              fontSize: 10,
              letterSpacing: "0.16em",
              textTransform: "none",
              color: M.inkSoft,
              cursor: "pointer",
              minHeight: 44,
            }}
          >
            Cancel
          </button>
        </div>
        <div style={{ padding: "14px 20px 0" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 3, paddingBottom: 6 }}>
            <span style={{ fontFamily: M.serif, fontSize: 34 }}>$</span>
            <span style={{ fontFamily: M.serif, fontSize: 50, lineHeight: 1 }}>{amount}</span>
          </div>
          <div
            style={{
              fontFamily: M.mono,
              fontSize: 10,
              letterSpacing: "0.18em",
              textTransform: "none",
              color: M.inkSoft,
              paddingBottom: 14,
            }}
          >
            {token} · ENCRYPTED BEFORE SENDING
          </div>
          {rows.map(([k, v, muted]) => (
            <div
              key={k}
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 16,
                padding: "13px 0",
                borderTop: `1px solid ${M.ruleSoft}`,
              }}
            >
              <span
                style={{
                  flex: "none",
                  paddingTop: 2,
                  fontFamily: M.mono,
                  fontSize: 9.5,
                  letterSpacing: "0.16em",
                  textTransform: "none",
                  color: M.inkSoft,
                }}
              >
                {k}
              </span>
              <span
                style={{
                  fontFamily: M.mono,
                  fontSize: 11,
                  lineHeight: 1.7,
                  color: muted ? M.inkSoft : M.ink,
                  wordBreak: "break-all",
                  textAlign: "right",
                }}
              >
                {v}
              </span>
            </div>
          ))}
        </div>
        <div
          style={{
            padding: `18px 20px calc(20px + ${SAFE_BOTTOM})`,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <Cta label="Confirm transfer" enabled onClick={onConfirm} />
          <div
            style={{
              textAlign: "center",
              fontFamily: M.mono,
              fontSize: 9.5,
              letterSpacing: "0.12em",
              textTransform: "none",
              color: M.inkFaint,
            }}
          >
            Amount is encrypted before it leaves this device
          </div>
        </div>
      </div>
    </>
  );
}

/* --------------------------------------------------- pending / complete */

export function MobilePending({
  title = "Generating proof",
  network,
  meta,
}) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 45,
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        padding: "0 40px",
        animation: "m-fade .18s ease both",
      }}
    >
      <MobileBase />
      <div
        style={{
          width: 62,
          height: 62,
          borderRadius: "50%",
          background: "#F6F6F3",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: "1.8px solid #D8E9F7",
            borderTopColor: M.link,
            animation: "m-spin .8s linear infinite",
          }}
        />
      </div>
      <div style={{ fontFamily: M.serif, fontSize: 31, textAlign: "center" }}>
        {title}
      </div>
      <div
        style={{
          fontFamily: M.mono,
          fontSize: 11,
          lineHeight: 1.85,
          color: "#8A8A85",
          textAlign: "center",
        }}
      >
        {meta ||
          `Encrypting the amount and submitting to ${network}. Keep the app open.`}
      </div>
    </div>
  );
}

export function MobileComplete({
  title = "Transfer complete",
  token,
  network,
  hash,
  onReceipt,
  onHistory,
  onReset,
  resetLabel = "Make another transfer",
  showReceipt = true,
  showHistory = true,
}) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 45,
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        animation: "m-fade .18s ease both",
      }}
    >
      <MobileBase />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 18,
          padding: "0 36px",
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "#EAF6FE",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 19,
              height: 11,
              marginTop: -5,
              borderLeft: "2px solid #2F87C4",
              borderBottom: "2px solid #2F87C4",
              transform: "rotate(-45deg)",
            }}
          />
        </div>
        <div style={{ fontFamily: M.serif, fontSize: 33, textAlign: "center" }}>
          {title}
        </div>
        <div
          style={{
            fontFamily: M.mono,
            fontSize: 10.5,
            letterSpacing: "0.1em",
            lineHeight: 2,
            color: "#8A8A85",
            textAlign: "center",
          }}
        >
          ENCRYPTED · {token}
          <br />
          {network} · {hash}
        </div>
      </div>
      <div
        style={{
          flex: "none",
          padding: `0 20px calc(22px + ${SAFE_BOTTOM})`,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {showReceipt && onReceipt && (
          <button
            type="button"
            className="m-cta"
            onClick={onReceipt}
            style={{
              minHeight: 58,
              border: `1px solid ${M.ink}`,
              borderRadius: 999,
              background: M.ink,
              color: "#FFFFFF",
              fontFamily: M.sans,
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            View receipt
          </button>
        )}
        {showHistory && onHistory && (
          <button
            type="button"
            className="m-cta"
            onClick={onHistory}
            style={{
              minHeight: 58,
              border: `1px solid ${M.ink}`,
              borderRadius: 999,
              background: "transparent",
              fontFamily: M.sans,
              fontSize: 16,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            View in history
          </button>
        )}
        <button
          type="button"
          onClick={onReset}
          style={{
            minHeight: 44,
            border: 0,
            background: "transparent",
            fontFamily: M.mono,
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "none",
            color: "#9A9A94",
            cursor: "pointer",
          }}
        >
          {resetLabel}
        </button>
      </div>
    </div>
  );
}

/** Compact claim strip for mobile — desktop uses the header / right banner. */
export function MobileClaimBanner({
  amountLabel,
  symbol,
  busy,
  onClaim,
  onDismiss,
}) {
  return (
    <div
      style={{
        flex: "none",
        margin: "0 16px 10px",
        padding: "14px 14px 12px",
        border: `1px solid ${M.rule}`,
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: M.serif,
              fontSize: 18,
              lineHeight: 1.2,
              marginBottom: 4,
            }}
          >
            Pending balance available
          </div>
          <div
            style={{
              fontFamily: M.mono,
              fontSize: 11,
              lineHeight: 1.55,
              color: M.inkSoft,
            }}
          >
            {amountLabel} C{symbol} ready to claim into your available balance.
          </div>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            style={{
              flex: "none",
              border: 0,
              background: "transparent",
              minWidth: 36,
              minHeight: 36,
              color: M.inkSoft,
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ✕
          </button>
        )}
      </div>
      <button
        type="button"
        className="m-cta"
        onClick={onClaim}
        disabled={busy}
        style={{
          minHeight: 48,
          border: 0,
          borderRadius: 999,
          background: busy ? "#F1F1ED" : M.accent,
          color: busy ? "#AFAFA9" : M.accentInk,
          fontFamily: M.sans,
          fontSize: 15,
          fontWeight: 600,
          cursor: busy ? "default" : "pointer",
        }}
      >
        {busy ? "Processing…" : "Secure Claim"}
      </button>
    </div>
  );
}

/* ==================================================================== shell */

export default function MobileShell({
  activeTab,
  onTab,

  connected,
  address,
  fullAddress,
  onWallet,
  onDisconnect,
  email,
  onSwitchAccount,
  switchingAccount,

  networkLabel,
  networkSelected,
  tokenIcon,
  chainIcon,
  onOpenNetwork,

  amount,
  onAmountChange,
  balanceLabel,
  isOver,
  overMessage = "Exceeds your confidential balance",

  recipient,
  onRecipientChange,
  recipientError,
  recipientNote = null,
  onUseDemoAccount,
  onScan,
  onClearRecipient,

  detailRows = [],
  percentEnabled,
  onPercent,

  ctaLabel,
  ctaEnabled,
  onSubmit,
  onRequestTransfer,

  claimBanner = null,

  children,
}) {
  const [amountFocused, setAmountFocused] = useState(false);
  const [recipientFocused, setRecipientFocused] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [addressCopied, setAddressCopied] = useState(false);

  // 100dvh doesn't reliably shrink when the on-screen keyboard opens on
  // every mobile browser (Android Chrome in particular can leave the
  // layout viewport at its pre-keyboard size) — anything sized off it
  // then sits partly behind the keyboard instead of resizing to clear it,
  // which is exactly what made the percent chips/CTA (while typing an
  // amount) and the bottom nav (once it reappears) feel like they were
  // floating over the keyboard instead of pinned above it. visualViewport
  // tracks the actually-visible height directly; falls back to 100dvh
  // (via null) wherever it isn't supported.
  const [viewport, setViewport] = useState(() =>
    typeof window !== "undefined" && window.visualViewport
      ? { height: window.visualViewport.height, offsetTop: window.visualViewport.offsetTop }
      : null,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const onChange = () => setViewport({ height: vv.height, offsetTop: vv.offsetTop });
    vv.addEventListener("resize", onChange);
    vv.addEventListener("scroll", onChange);
    return () => {
      vv.removeEventListener("resize", onChange);
      vv.removeEventListener("scroll", onChange);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!(amountFocused || recipientFocused)) return;
    const { body } = document;
    const html = document.documentElement;
    const prev = [body.style.overflow, html.style.overflow, body.style.overscrollBehavior];
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    return () => {
      body.style.overflow = prev[0];
      html.style.overflow = prev[1];
      body.style.overscrollBehavior = prev[2];
    };
  }, [amountFocused, recipientFocused]);

  const isTransfer = activeTab === "transfer";
  const isHistory = activeTab === "history";
  // Native keyboard is up for EITHER field (amount or recipient) — compact
  // the layout: hide the bottom nav, show a back affordance, pin the CTA
  // above the keyboard.
  const isEditing = (amountFocused || recipientFocused) && !isHistory;
  const has = amount !== "" && amount != null;
  const numColor = isOver ? M.danger : has ? M.ink : M.ghost;

  const shortAddr = recipient
    ? recipient.slice(0, 8) + "…" + recipient.slice(-6)
    : "Address";

  return (
    <div
      className="m-app"
      style={{
        // Pinned to the VISUAL viewport, not the page. When the on-screen
        // keyboard opens, iOS shrinks the visual viewport but keeps the page
        // scrollable and scrolls it to reveal the focused field — a
        // position:relative shell rides up with that scroll and strands the
        // bottom nav mid-screen. Fixing the shell to the visualViewport
        // height + offset keeps the header pinned top and the nav pinned just
        // above the keyboard; only the inner content column scrolls. Falls
        // back to relative + 100dvh where visualViewport isn't supported.
        position: viewport ? "fixed" : "relative",
        top: viewport ? viewport.offsetTop : undefined,
        left: 0,
        right: 0,
        height: viewport ? `${viewport.height}px` : "100dvh",
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
        color: M.ink,
        fontFamily: M.sans,
        overflow: "hidden",
        paddingTop: SAFE_TOP,
      }}
    >
      <MobileBase />
      <Header
        connected={connected}
        onHome={() => onTab && onTab("transfer")}
        address={address}
        onWallet={connected ? () => setShowAccountMenu(true) : onWallet}
        showBack={isEditing}
        onBack={() => {
          // Closes the keyboard (blurring the input fires the existing
          // onBlur) and drops back into the normal, bottom-nav-visible
          // layout — there's nothing to actually navigate to.
          if (
            typeof document !== "undefined" &&
            document.activeElement &&
            document.activeElement.blur
          ) {
            document.activeElement.blur();
          }
          setAmountFocused(false);
          setRecipientFocused(false);
        }}
      />

      {isHistory ? (
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{children}</div>
      ) : (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflowX: "hidden", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {claimBanner}
          {/* network row — anchored right under the header, not centered
              with the amount. It used to be the first child of the centered
              amount zone below, which put it floating near the middle of
              the screen instead of reading as a fixed setting up top. */}
          <div style={{ flex: "none", padding: "14px 20px 0" }}>
            <button
              type="button"
              onClick={onOpenNetwork}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minHeight: 44,
                padding: "9px 14px",
                border: `1px solid ${M.rule}`,
                background: "transparent",
                fontFamily: M.mono,
                fontSize: 10.5,
                letterSpacing: "0.14em",
                textTransform: "none",
                color: M.inkMid,
                cursor: "pointer",
              }}
            >
              {networkSelected && tokenIcon ? (
                // Token icon with the chain as a small overlapping badge —
                // same idea as the reference's stacked chain/token icon,
                // just real asset icons at a scale that stays a detail
                // instead of becoming the focal point of the row.
                <span style={{ position: "relative", width: 20, height: 20, flex: "none" }}>
                  <img
                    src={tokenIcon}
                    alt=""
                    style={{ width: 20, height: 20, borderRadius: "50%", display: "block" }}
                  />
                  {chainIcon && (
                    <img
                      src={chainIcon}
                      alt=""
                      style={{
                        position: "absolute",
                        right: -3,
                        bottom: -3,
                        width: 11,
                        height: 11,
                        borderRadius: "50%",
                        border: "1.5px solid #FFFFFF",
                        display: "block",
                      }}
                    />
                  )}
                </span>
              ) : (
                networkSelected && (
                  <span
                    style={{ width: 6, height: 6, borderRadius: "50%", background: "#4EC08A", flex: "none" }}
                  />
                )
              )}
              <span>{networkLabel}</span>
              <span
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: "4px solid transparent",
                  borderRight: "4px solid transparent",
                  borderTop: "4.5px solid #9A9A94",
                }}
              />
            </button>
          </div>

          {/* amount zone — paddingTop keeps the big numeral clear of the
              network pill on short phones where flex centering would
              otherwise collapse the gap. */}
          <div
            style={{
              flex: isEditing ? "0 0 auto" : 1,
              flexShrink: isEditing ? 1 : 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
              padding: isEditing ? "12px 20px 8px" : "24px 20px 16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 3, padding: "4px 0" }}>
              <span style={{ fontFamily: M.serif, fontSize: 38, color: numColor }}>$</span>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="0"
                value={amount || ""}
                onChange={(e) => onAmountChange(e.target.value)}
                onFocus={() => setAmountFocused(true)}
                onBlur={() => setAmountFocused(false)}
                className="m-amount-input"
                style={{
                  fontFamily: M.serif,
                  fontSize: 74,
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  color: numColor,
                  border: 0,
                  outline: "none",
                  background: "transparent",
                  padding: 0,
                  // Grows with the typed value instead of a fixed box — no
                  // field-sizing dependency, so it's just as correct in
                  // Safari as anywhere else. Exactly content-width, no
                  // trailing buffer: a reserved-space buffer here sits to
                  // the right of the visible glyphs (text-align is left),
                  // so the row's centered box would carry dead space the
                  // eye doesn't see as part of "$0" — reads as off-center
                  // even though the box itself is dead center.
                  width: `${Math.max(has ? amount.length : 1, 1)}ch`,
                }}
              />
            </div>

            <div
              style={{
                fontFamily: M.mono,
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "none",
                color: M.inkSoft,
              }}
            >
              {balanceLabel}
            </div>
            <div
              style={{
                fontFamily: M.mono,
                fontSize: 10,
                letterSpacing: "0.12em",
                textTransform: "none",
                color: isOver ? M.danger : "transparent",
              }}
            >
              {isOver ? overMessage : "\u00A0"}
            </div>
          </div>

          {/* recipient row — editable address input (demo/clear as shortcuts) */}
          {isTransfer && (
            <div style={{ flex: "none", padding: "0 20px 12px" }}>
              <div
                style={{
                  border: `1px solid ${recipientError ? M.danger : M.rule}`,
                  minHeight: 48,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "0 8px 0 16px",
                }}
              >
                <input
                  className="m-recipient-input"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder="Enter receiver address"
                  value={recipient || ""}
                  onChange={(e) => onRecipientChange?.(e.target.value)}
                  onFocus={() => setRecipientFocused(true)}
                  onBlur={() => setRecipientFocused(false)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 0,
                    outline: "none",
                    background: "transparent",
                    padding: 0,
                    fontFamily: M.mono,
                    fontSize: 12.5,
                    letterSpacing: "0.02em",
                    color: recipientError ? M.danger : M.ink,
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 4, flex: "none" }}>
                  {recipient ? (
                    <button
                      type="button"
                      onClick={onClearRecipient}
                      style={{
                        minWidth: 56,
                        minHeight: 48,
                        border: 0,
                        background: "transparent",
                        fontFamily: M.mono,
                        fontSize: 10,
                        letterSpacing: "0.14em",
                        textTransform: "none",
                        color: M.inkSoft,
                        cursor: "pointer",
                      }}
                    >
                      Clear
                    </button>
                  ) : (
                    <>
                      {onUseDemoAccount && (
                        <button
                          type="button"
                          onClick={onUseDemoAccount}
                          style={{
                            minHeight: 44,
                            padding: "0 2px",
                            border: 0,
                            background: "transparent",
                            fontFamily: M.mono,
                            fontSize: 10,
                            letterSpacing: "0.12em",
                            textTransform: "none",
                            color: M.link,
                            cursor: "pointer",
                          }}
                        >
                          Demo address
                        </button>
                      )}
                      {onScan && (
                        <button
                          type="button"
                          onClick={onScan}
                          style={{
                            minWidth: 56,
                            minHeight: 48,
                            border: `1px solid ${M.rule}`,
                            background: "transparent",
                            fontFamily: M.mono,
                            fontSize: 10,
                            letterSpacing: "0.12em",
                            textTransform: "none",
                            color: M.inkMid,
                            cursor: "pointer",
                          }}
                        >
                          Scan
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {recipientNote ? (
                <div style={{ marginTop: 12 }}>{recipientNote}</div>
              ) : null}
            </div>
          )}

          <div style={{ flex: "none" }}>
            <div style={{ display: "flex", gap: 8, padding: "0 20px 12px" }}>
              {["20%", "50%", "75%", "Max"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => percentEnabled && onPercent(p === "Max" ? "max" : parseInt(p, 10))}
                  style={{
                    flex: 1,
                    minHeight: 44,
                    border: "1px solid #EAEAE5",
                    background: "transparent",
                    fontFamily: M.mono,
                    fontSize: 11,
                    color: percentEnabled ? M.inkMid : "#BDBDB7",
                    cursor: percentEnabled ? "pointer" : "default",
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
            {!isEditing && (
              <div style={{ padding: "0 20px 8px" }}>
                {detailRows.map(([k, v]) => (
                  <div
                    key={k}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "11px 0",
                      borderTop: `1px solid ${M.ruleSoft}`,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: M.mono,
                        fontSize: 9.5,
                        letterSpacing: "0.16em",
                        textTransform: "none",
                        color: M.inkSoft,
                      }}
                    >
                      {k}
                    </span>
                    <span style={{ fontFamily: M.mono, fontSize: 11.5, color: M.ink }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ padding: isEditing ? `0 20px calc(10px + ${SAFE_BOTTOM})` : "4px 20px 14px" }}>
              <Cta label={ctaLabel} enabled={ctaEnabled} onClick={onSubmit} />
              {isTransfer && onRequestTransfer && (
                <button
                  type="button"
                  onClick={onRequestTransfer}
                  style={{ display: "block", margin: "10px auto 0", background: "none", border: "none", color: M.link, cursor: "pointer", fontFamily: M.mono, fontSize: 12.5, padding: 4 }}
                >
                  Request a Payment
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!isEditing && <BottomNav activeTab={activeTab} onTab={onTab} />}

      {showAccountMenu && (
        // Tapping the address used to disconnect immediately — this is
        // the account-details dropdown desktop already has (address,
        // copy, disconnect), just as a sheet instead of a popover menu.
        <Sheet title="Account" actionLabel="Close" onAction={() => setShowAccountMenu(false)}>
          {email ? (
            <div style={{ padding: "2px 20px 12px" }}>
              <div
                style={{
                  fontFamily: M.mono,
                  fontSize: 9.5,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: M.inkSoft,
                  marginBottom: 5,
                }}
              >
                Signed in as
              </div>
              <div style={{ fontFamily: M.sans, fontSize: 15, color: M.ink, wordBreak: "break-all" }}>
                {email}
              </div>
            </div>
          ) : null}
          <div style={{ padding: "2px 20px 16px" }}>
            <div
              style={{
                fontFamily: M.mono,
                fontSize: 12.5,
                letterSpacing: "0.02em",
                color: M.ink,
                wordBreak: "break-all",
              }}
            >
              {fullAddress}
            </div>
          </div>
          <button
            type="button"
            className="m-row"
            onClick={() => {
              if (!fullAddress || typeof navigator === "undefined" || !navigator.clipboard) return;
              navigator.clipboard
                .writeText(fullAddress)
                .then(() => {
                  setAddressCopied(true);
                  setTimeout(() => setAddressCopied(false), 2000);
                })
                .catch(() => {});
            }}
            style={{
              width: "100%",
              minHeight: 56,
              padding: "0 20px",
              border: 0,
              borderBottom: `1px solid ${M.ruleInner}`,
              background: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 14,
              fontFamily: M.sans,
              fontSize: 15,
              color: M.ink,
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={M.inkMid} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              {addressCopied ? (
                <path d="M5 12l5 5 9-9" />
              ) : (
                <>
                  <rect x="8" y="8" width="12" height="12" rx="1.5" />
                  <path d="M16 8V5.5A1.5 1.5 0 0014.5 4H5.5A1.5 1.5 0 004 5.5v9A1.5 1.5 0 005.5 16H8" />
                </>
              )}
            </svg>
            {addressCopied ? "Address copied" : "Copy address"}
          </button>
          {email && onSwitchAccount ? (
            <button
              type="button"
              className="m-row"
              disabled={switchingAccount}
              onClick={() => {
                setShowAccountMenu(false);
                onSwitchAccount();
              }}
              style={{
                width: "100%",
                minHeight: 56,
                padding: "0 20px",
                border: 0,
                borderBottom: `1px solid ${M.ruleInner}`,
                background: "transparent",
                display: "flex",
                alignItems: "center",
                gap: 14,
                fontFamily: M.sans,
                fontSize: 15,
                color: M.ink,
                textAlign: "left",
                cursor: switchingAccount ? "default" : "pointer",
                opacity: switchingAccount ? 0.6 : 1,
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={M.inkMid} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 8h13l-3-3M20 16H7l3 3" />
              </svg>
              {switchingAccount ? "Switching…" : "Change account"}
            </button>
          ) : null}
          <button
            type="button"
            className="m-row"
            onClick={() => {
              setShowAccountMenu(false);
              onDisconnect?.();
            }}
            style={{
              width: "100%",
              minHeight: 56,
              padding: "0 20px",
              border: 0,
              borderBottom: `1px solid ${M.ruleInner}`,
              background: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 14,
              fontFamily: M.sans,
              fontSize: 15,
              color: M.danger,
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={M.danger} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3" />
              <path d="M10 8l-4 4 4 4" />
              <path d="M6 12h12" />
            </svg>
            Disconnect wallet
          </button>
        </Sheet>
      )}
    </div>
  );
}
